import { useMemo, useState } from 'react'
import { Pencil } from 'lucide-react'
import {
  Button,
  DataTable,
  SearchInput,
  toast,
  type DataTableColumn,
  type DataTableExpansion,
} from '@atrvd/saas-ui'
import { formatCurrency, formatDate } from '@dcp/shared/utils/formatters'
import { hasPermission } from '@dcp/shared'
import { ViewBankTransactionsDialog } from '@/components/banking/ViewBankTransactionsDialog'
import { ViewTransactionsIcon } from '@/components/banking/ViewTransactionsIcon'
import { UpdateLocAvailabilityDialog } from '@/components/banking/UpdateLocAvailabilityDialog'
import {
  locAvailabilityRows,
  locDrillFor,
  type LocAvailabilityRow,
  type LocDrill,
} from '@/components/banking/locAvailabilityRows'
import { queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import {
  useLocAvailability,
  useLocTransactions,
  useUpdateBorrowingBase,
  useUpdatePackBorrowingBase,
} from '@/hooks/useBanking'
import { useProfile } from '@/hooks/useProfile'
import { useTablePagination } from '@/hooks/useTablePagination'
import type { LocReference } from '@/types/loc-availability'

const NO_REFERENCES: LocReference[] = []

function rowName(row: LocAvailabilityRow): string {
  return row.kind === 'reference' ? row.reference.name : row.line.name
}

/** Sub-row keys come out as `{referenceId}:{lineId}`, stable across pages and refetches. */
function rowKey(row: LocAvailabilityRow): string {
  return row.kind === 'reference' ? row.reference.id : row.line.id
}

/** Expansion state stays inside DataTable: it is keyed by reference id and survives search and paging. */
const EXPANSION: DataTableExpansion<LocAvailabilityRow> = {
  subRows: (row) => (row.kind === 'reference' ? row.lineRows : []),
  // A stable name; `aria-expanded` carries the open/closed state.
  toggleLabel: (row) => `${rowName(row)} lines`,
}

/** Reference rows set their amounts in medium weight; line rows stay regular. */
function referenceEmphasis(row: LocAvailabilityRow): string | undefined {
  return row.kind === 'reference' ? 'font-medium' : undefined
}

/**
 * Line of Credit Availability page: borrowing base and LOC usage per reference.
 *
 * References come from the `get_loc_availability` RPC via
 * {@link useLocAvailability}. Editing a standalone facility's Borrowing Base
 * persists through {@link useUpdateBorrowingBase}; editing a pack's full
 * multi-line set persists through {@link useUpdatePackBorrowingBase}.
 * A reference with ≥2 lines expands into DataTable sub-rows, one per LOC line:
 * they share the reference columns and are paged with their parent, never on
 * their own. LOC Usage is the derived total from the RPC, and the "View bank
 * transactions" drill shows the LINE OF CREDIT transactions behind it.
 */
export function LocAvailabilityPage() {
  const availability = useLocAvailability()

  /**
   * RBAC: only roles with `manage_loc` (accounts_payable, treasury,
   * admin) may edit the LOC borrowing base. `view_only` sees the same table but
   * the Edit affordance is hidden. manage_loc is separate from manage_banking
   * so Treasury can edit LOC without managing general banking (a temporary BA
   * constraint). Uses the same helper `RequirePermission` uses.
   */
  const { data: profile } = useProfile()
  const canManage = hasPermission(profile?.productRole, 'manage_loc')

  /** Persistence for a standalone facility's single LOC line. */
  const updateBorrowingBase = useUpdateBorrowingBase()

  /** Persistence for a pack's full multi-line borrowing base. */
  const updatePackBorrowingBase = useUpdatePackBorrowingBase()

  /** True while either save mutation is in flight — disables the dialog's Update button to prevent a double-submit. */
  const isSaving = updateBorrowingBase.isPending || updatePackBorrowingBase.isPending

  /** Client-side search over reference name and member facility names (AC.5). */
  const [search, setSearch] = useState('')

  /** Active "View bank transactions" drill-down, or null. */
  const [txnDrill, setTxnDrill] = useState<LocDrill | null>(null)

  /**
   * LINE OF CREDIT transactions behind the OPEN drill-down, and nothing
   * else. Passing the scope (null while closed)
   * keeps the hook disabled until a pop-up opens and then asks the RPC for that
   * one reference's rows, instead of the org-wide feed PostgREST silently
   * truncated at 1000 rows. The fetch starts on click, so the drill buttons
   * have no disabled state; loading and errors show inside the modal.
   */
  const drill = useLocTransactions(txnDrill?.scope ?? null)

  /** Reference selected for the Update dialog, or null when closed. */
  const [editReference, setEditReference] = useState<LocReference | null>(null)

  const references = availability.data ?? NO_REFERENCES
  const rows = locAvailabilityRows(references, search)

  /**
   * Paged by REFERENCE: line sub-rows render with their parent and never count
   * toward the page size, so expanding a pack can't push another reference
   * onto the next page.
   */
  const pagination = useTablePagination({ totalItems: rows.length, resetKey: search.trim() })
  const tableState = tableQueryState(availability, rows.length)
  const errorDisplay = queryErrorDisplay(tableState, rows.length)

  const columns = useMemo<DataTableColumn<LocAvailabilityRow>[]>(
    () => [
      {
        key: 'reference',
        header: 'Reference',
        // Line rows list every facility of the line; without a floor they wrap
        // to one name per line. The table scrolls sideways inside its card.
        minWidth: '240px',
        render: (row) =>
          row.kind === 'reference' ? (
            <span className="font-medium">{row.reference.name}</span>
          ) : (
            // A block, so the indent holds on every wrapped line of a long facility list.
            <div className="min-w-[200px] whitespace-normal pl-4 text-sm text-muted-foreground">{row.line.facilities.join(', ')}</div>
          ),
      },
      {
        key: 'borrowingBaseAmount',
        header: 'Borrowing Base Amount',
        cellClassName: referenceEmphasis,
        render: (row) => formatCurrency(row.figures.borrowingBaseAmount),
      },
      {
        key: 'locUsage',
        header: 'LOC Usage',
        render: (row) => (
          <div className="flex items-center gap-1">
            {formatCurrency(row.figures.locUsage)}
            {/* Shown on every row, not on hover. A reference row drills into all
                of its lines; a line row into that line only. */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setTxnDrill(locDrillFor(row))}
              title="View bank transactions"
              aria-label={`View bank transactions for ${rowName(row)}`}
              className="h-6 w-6"
            >
              <ViewTransactionsIcon className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
      },
      {
        key: 'remaining',
        header: 'Remaining Credit Availability',
        cellClassName: referenceEmphasis,
        render: (row) => formatCurrency(row.figures.remaining),
      },
      // AC.11: a single-line reference shows its line's values; a
      // multi-line reference shows "—" (its lines carry them).
      {
        key: 'effectiveDate',
        header: 'Date Effective',
        render: (row) => (row.detailLine?.effectiveDate ? formatDate(row.detailLine.effectiveDate) : '—'),
      },
      {
        key: 'note',
        header: 'Notes',
        minWidth: '200px',
        render: (row) => row.detailLine?.note || '—',
      },
      {
        key: 'startingLocUsageDate',
        header: 'Starting LOC Usage Date',
        render: (row) =>
          row.detailLine?.startingLocUsageDate ? formatDate(row.detailLine.startingLocUsageDate) : '—',
      },
      {
        key: 'startingLocUsageAmount',
        header: 'Starting LOC Usage Amount',
        render: (row) =>
          row.detailLine?.startingLocUsageAmount != null
            ? formatCurrency(row.detailLine.startingLocUsageAmount)
            : '—',
      },
      {
        key: 'actions',
        header: 'Actions',
        // Facilities and packs are both edited through the Update dialog; line
        // rows have no actions of their own.
        render: (row) =>
          canManage && row.kind === 'reference' ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setEditReference(row.reference)}
              aria-label={`Edit ${row.reference.name}`}
              className="h-6 w-6"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : null,
      },
    ],
    [canManage],
  )

  /**
   * Persist the edited reference. A `pack` writes its full desired
   * line set through the replace-semantics RPC; a `facility` upserts its single
   * line. Both mutations' settle-time invalidation refetches the table.
   *
   * On success the dialog closes. On failure it is left OPEN so the user can
   * retry without re-entering their edits — the mutation's own `onError`
   * already surfaces a toast, so the error is swallowed here rather than
   * rethrown (the dialog awaits this promise but treats any rejection as
   * already-handled).
   */
  const handleSave = async (updated: LocReference) => {
    try {
      if (updated.type === 'pack') {
        await updatePackBorrowingBase.mutateAsync({
          packId: updated.id,
          lines: updated.lines,
        })
      } else {
        // A standalone facility must resolve to EXACTLY one line: useLocAvailability
        // synthesizes one when none exists, and the DB's UNIQUE(facility_id) caps it
        // at one. Anything else is an out-of-spec data anomaly — and the single-line
        // upsert below only persists lines[0], so it would silently (a) save nothing
        // for 0 lines while the dialog closed as if it succeeded, or (b) drop every
        // line past the first. Fail loudly and keep the dialog open instead.
        if (updated.lines.length !== 1) {
          toast.error('Unable to save: a facility must have exactly one line of credit.')
          return
        }
        const line = updated.lines[0]
        await updateBorrowingBase.mutateAsync({
          facilityId: updated.id,
          line: {
            name: line.name,
            borrowingBaseAmount: line.borrowingBaseAmount,
            effectiveDate: line.effectiveDate,
            note: line.note,
            startingLocUsageDate: line.startingLocUsageDate,
            startingLocUsageAmount: line.startingLocUsageAmount,
          },
        })
      }
      setEditReference(null)
    } catch {
      // Error already surfaced via the mutation's onError toast; keep the
      // dialog open so the user can retry instead of losing their edits.
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Line of Credit Availability</h1>
        <p className="text-muted-foreground">
          Borrowing base and starting usage input by pack and facility
        </p>
      </div>

      {/* Client-side search over reference and member facility names (AC.5). */}
      <SearchInput
        placeholder="Search by pack or facility name..."
        value={search}
        onChange={setSearch}
        clearable
        className="max-w-sm"
      />

      {errorDisplay.banner}
      <DataTable
        columns={columns}
        data={pagination.paginate(rows)}
        rowKey={rowKey}
        expansion={EXPANSION}
        showColumnControls={false}
        itemName="references"
        pagination={pagination.dataTablePagination}
        loading={tableState.loading}
        refreshing={tableState.refreshing}
        error={errorDisplay.tableError}
        emptyState={
          <p className="text-sm text-muted-foreground">
            {/* "This filter" only when there is one: an org with no active
                facilities or packs has nothing to show at all. */}
            {search.trim()
              ? 'No borrowing base records match this filter.'
              : 'No borrowing base records yet.'}
          </p>
        }
      />

      {/* "View bank transactions" drill-down modal: the LINE OF CREDIT
          transactions behind the clicked LOC Usage, fetched for this scope only
          while the modal is open — hence the loading/error/reconciliation props. */}
      {txnDrill && (
        <ViewBankTransactionsDialog
          open
          onClose={() => setTxnDrill(null)}
          referenceName={txnDrill.title}
          groups={drill.data ?? []}
          // `isLoading`, NOT `isPending`: in React Query v5 `isPending` only means
          // "no data yet", so it is also true for a DISABLED query — it would read
          // as loading for a scope nobody asked for. `isLoading` is defined as
          // `isPending && isFetching`, i.e. true only while the first real fetch
          // for this scope is in flight. It is also false during a background
          // refetch over cached rows, so saving an inline Note refreshes the feed
          // without blanking the table the user is editing.
          isLoading={drill.isLoading}
          // Only surface the error when there is genuinely nothing to show: a
          // failed background refetch keeps the previous `data` (React Query v5),
          // and those stale-but-valid rows stay visible with `refreshFailed`.
          errorMessage={drill.isError && !drill.data ? drill.error.message : undefined}
          refreshFailed={drill.isError && !!drill.data}
          onRetry={() => void drill.refetch()}
          retrying={drill.isFetching}
          // Suppress the modal's own missing-transactions check while the
          // reference table itself is fetching. `startingLocUsageAmount`
          // (used by both this reference's LOC Usage aggregate and its baseline)
          // is written to the cache optimistically by useUpdateBorrowingBase /
          // useUpdatePackBorrowingBase, deliberately ahead of the derived LOC
          // Usage recompute that only lands on the settle-time refetch. During
          // that ~0.5-2s window the drilled LOC Usage (this drill's expectedTotal)
          // can transiently disagree with an empty transaction list for a facility
          // with none — a false alarm, not a real data-integrity signal. `undefined`
          // already means "skip the check" to the modal, so we simply withhold the
          // figure until the source query settles.
          expectedTotal={availability.isFetching ? undefined : txnDrill.expectedTotal}
          showGroupHeadings={txnDrill.showGroupHeadings}
          startingBalance={txnDrill.startingBalance}
        />
      )}

      {/* "Update Line of Credit Availability" modal. */}
      <UpdateLocAvailabilityDialog
        open={editReference !== null}
        reference={editReference}
        onClose={() => setEditReference(null)}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </div>
  )
}
